/**
 * Platform and author-scope controls for the social analytics dashboard.
 *
 * ## Why platform is an ENTITY filter
 *
 * A platform lives in two places: on the entity (`auto_coder_entities.platform`,
 * the video/post) and on the record (`metadata.platform`, the comment). Only the
 * entity level reaches BOTH halves of this page:
 *
 *   - comment charts / comment list — `LabelQuery.entity` lowers to an
 *     `entity_id IN (…)` semi-join over the facts table;
 *   - the KPI strip, cultural relevance and meaningful sentiment — those
 *     endpoints take an `EntityQuery` and nothing else.
 *
 * A record-level `metadata.platform` filter would narrow the charts and leave
 * every video metric untouched: the same number of views and the same
 * engagement rate, beside a chart that just changed. That is the silent
 * disagreement the entity work existed to remove, so platform is entity-level,
 * full stop.
 *
 * ## Why "all platforms" must send NOTHING
 *
 * Any non-null entity query is a semi-join, so it also drops records with no
 * entity at all (`entity_id IS NULL` — every Facebook comment today, because
 * `pkg/videoid` has no Facebook extractor). "All platforms" therefore cannot be
 * expressed as "every platform in the list": that would silently delete the
 * parentless comments from a page the user believes is unfiltered. `{mode:'all'}`
 * emits no platform constraint whatsoever.
 *
 * ## Why the author scope is a record-level EXCLUSION
 *
 * `author_type` is a property of the comment's author, not of the video, so it
 * has no entity analogue — it narrows the comment surfaces and deliberately
 * leaves the video metrics alone (a video's reach does not depend on which of
 * its comments you count). The default is audience-only, and it is expressed as
 * "exclude brand", never as "require audience": the contract is that a record
 * with no `author_type` IS audience, and an equality filter would erase every
 * record ingested before author typing shipped.
 *
 * Everything here is pure so it can be unit-tested without React.
 */

import type { LabelValue, PlatformToggleCounts } from '../../types/gotypes'

export type { PlatformToggleCounts }

// ── Platform selection ───────────────────────────────────────────────────────

/**
 * `all` is not "every platform ticked" — it is the absence of a constraint.
 *
 * ⚠ THE CONTROL IS SINGLE-SELECT (Kai, 2026-08-14: "we should have all, and
 * it's the default, but it's a single select… you click on the one you want to
 * see, as opposed to at the moment, [where] switching between them is quite a
 * lot"). The multi-select shape survives in this type because it is also the
 * WIRE shape (`EntityQuery.platforms` is a list) and because two of the three
 * states — all, and one platform — are exactly what it already expressed. What
 * cannot happen any more is a subset of any size other than one:
 * `normalizePlatformSelection` folds every other subset, including the empty
 * one, back to `all`. See the note there.
 */
export type PlatformSelection = { mode: 'all' } | { mode: 'subset'; platforms: string[] }

export const ALL_PLATFORMS: PlatformSelection = { mode: 'all' }

/**
 * The value the single-select control holds: a platform key, or this sentinel
 * for "All". It is a control-layer value only — nothing sends it to the server,
 * where "all" is expressed by sending no platform constraint whatsoever.
 */
export const ALL_PLATFORMS_CHOICE = 'all'

export type PlatformChoice = string

export interface PlatformOption {
  value: string
  label: string
  /** Videos on this platform, from the entity filter summary. */
  videos: number
  /**
   * Comments this platform's button is worth, or null when the mount did not
   * supply them (an older server, or the job-wide mount).
   *
   * 🔴 NULL IS NOT ZERO. A missing count renders no chip at all; a real zero
   * renders "0". A platform with no conversation and a platform we did not ask
   * about are different facts, and the second one must not be reported as the
   * first.
   */
  comments: number | null
}

/**
 * Display names for the platforms we have seen. This map is a SPELLING aid
 * only — it never decides which toggles exist. The options come from the job's
 * own data (`/entities/filter-summary`), so a platform nobody has written code
 * for still gets a toggle, with a titlecased label.
 */
const PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'Twitter',
  x: 'X',
  threads: 'Threads',
  reddit: 'Reddit',
  snapchat: 'Snapchat',
  twitch: 'Twitch',
}

/** Human label for a platform key, falling back to a titlecase of the key. */
export function platformLabel(value: string): string {
  const known = PLATFORM_LABELS[value.toLowerCase()]
  if (known) return known
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Turn the filter-summary's platform list into toggle options, busiest first.
 *
 * 🔴 THE TWO ARGUMENTS ARE DIFFERENT QUESTIONS AND BOTH ARE NEEDED.
 *
 *   - `platforms` is the UNFILTERED universe. It decides WHICH toggles exist
 *     and in what order, and it must: derived from the filtered counts instead,
 *     choosing TikTok would delete the other three buttons and strand the user
 *     with no way back. It also fixes the ORDER, so the toggles do not
 *     reshuffle under the cursor every time a filter moves.
 *   - `counts` is what each button is worth RIGHT NOW, under the page's
 *     filters. A platform absent from it has genuinely none left (the query
 *     groups the rows that exist), so it reads 0 — but the whole object being
 *     absent means nobody has answered yet, which reads null.
 */
export function derivePlatformOptions(
  platforms: LabelValue[],
  counts?: PlatformToggleCounts,
): PlatformOption[] {
  const videos = new Map((counts?.videos ?? []).map((v) => [v.value, v.count ?? 0]))
  const comments = new Map((counts?.comments ?? []).map((c) => [c.value, c.count ?? 0]))
  return [...platforms]
    // Sorted BEFORE the counts are attached, so the order is the universe's and
    // never the filtered numbers'.
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.value.localeCompare(b.value))
    .map((p) => ({
      value: p.value,
      label: platformLabel(p.value),
      videos: counts ? (videos.get(p.value) ?? 0) : (p.count ?? 0),
      comments: counts ? (comments.get(p.value) ?? 0) : null,
    }))
}

const subset = (platforms: string[]): PlatformSelection => ({
  mode: 'subset',
  // De-duplicated as well as sorted: a stored (or hand-edited) subset carrying
  // the same platform twice must not read as "two of two platforms" and
  // collapse to `all` below — that would silently unfilter the page.
  platforms: [...new Set(platforms)].sort(),
})

/**
 * Reconcile a selection (typically restored from storage) with the platforms
 * the mount actually has: unknown platforms are dropped, and anything that is
 * not exactly one surviving platform becomes `all`.
 *
 * While `available` is still empty (summary loading) the selection is returned
 * untouched — collapsing it then would silently widen the user's filter.
 *
 * 🔴 "NOT EXACTLY ONE ⇒ ALL" IS THE SINGLE-SELECT RULE, AND IT REPLACES TWO
 * OLDER ONES. Both were right for a multi-select control and are wrong for this
 * one:
 *
 *   - "a subset covering everything collapses to all" — ticking every box did
 *     mean all. Now there are no boxes: choosing the only platform a mount has
 *     is a deliberate exclusive choice, and it must NOT widen to `all`, because
 *     `all` also includes the comments whose parent post we could not identify.
 *     Under the old rule a one-platform department silently gained them back;
 *     the count on the button would then have promised one number and the page
 *     delivered another.
 *   - an EMPTY subset used to survive as a real "show nothing" state. The
 *     control can no longer express it (there is always a button lit), so a
 *     stored one would be a filter with no control and no way to clear it —
 *     strictly worse than the widening. It resets.
 *
 * A subset of two or more can now only come from storage written by the old
 * control, and it resets for the same reason: the user can see `All` is lit and
 * can see what they get, which a half-represented multi-select would not give.
 */
export function normalizePlatformSelection(
  selection: PlatformSelection,
  available: string[],
): PlatformSelection {
  if (selection.mode === 'all') return ALL_PLATFORMS
  if (available.length === 0) return selection
  const kept = [...new Set(selection.platforms.filter((p) => available.includes(p)))]
  if (kept.length !== 1) return ALL_PLATFORMS
  return subset(kept)
}

/** The value the single-select control shows for a selection. */
export function platformChoice(selection: PlatformSelection): PlatformChoice {
  if (selection.mode === 'all' || selection.platforms.length !== 1) return ALL_PLATFORMS_CHOICE
  return selection.platforms[0]
}

/** The selection a click on one of the control's buttons produces. */
export function choosePlatform(choice: PlatformChoice): PlatformSelection {
  if (choice === ALL_PLATFORMS_CHOICE) return ALL_PLATFORMS
  return subset([choice])
}

/**
 * The value for `EntityQuery.platforms`. An empty array means "no constraint" —
 * which is what `buildEntityQuery` already omits, and what the Go lowering
 * treats as absent (`if len(q.Platforms) > 0`).
 */
export function platformsForQuery(selection: PlatformSelection, available: string[]): string[] {
  const normalized = normalizePlatformSelection(selection, available)
  return normalized.mode === 'all' ? [] : [...normalized.platforms].sort()
}

/** Under `all`, every platform reads as on — that is what the user sees. */
export function isPlatformSelected(selection: PlatformSelection, platform: string): boolean {
  return selection.mode === 'all' || selection.platforms.includes(platform)
}

export function selectAllPlatforms(): PlatformSelection {
  return ALL_PLATFORMS
}

// ── Author scope ─────────────────────────────────────────────────────────────

export type AuthorScope = 'audience' | 'brand' | 'all'

/** The record-metadata key chain A stamps: "brand" | "audience". */
export const AUTHOR_TYPE_KEY = 'author_type'

/** The brand value; anything else (including absent) counts as audience. */
export const AUTHOR_TYPE_BRAND = 'brand'

/**
 * The dashboard opens on audience-only. 9% of Sky's comments are replies from
 * Sky's own accounts; counted as audience they inflate positive sentiment and
 * every "Sky Relevant / Sky Attributed" percentage on the page. The page states
 * the scope permanently in the toolbar, so the default is declared, not hidden.
 */
export const DEFAULT_AUTHOR_SCOPE: AuthorScope = 'audience'

/** The record-level halves of a LabelQuery implied by an author scope. */
export interface AuthorScopeQuery {
  metadataFilters?: Record<string, string>
  metadataExclusions?: Record<string, string[]>
}

export function authorScopeQuery(scope: AuthorScope): AuthorScopeQuery {
  switch (scope) {
    case 'audience':
      return { metadataExclusions: { [AUTHOR_TYPE_KEY]: [AUTHOR_TYPE_BRAND] } }
    case 'brand':
      return { metadataFilters: { [AUTHOR_TYPE_KEY]: AUTHOR_TYPE_BRAND } }
    default:
      return {}
  }
}

export function describeAuthorScope(scope: AuthorScope): string {
  switch (scope) {
    case 'audience':
      return 'Audience only — replies from the brand’s own accounts are excluded'
    case 'brand':
      return 'Brand replies only — comments posted by the brand’s own accounts'
    default:
      return 'All authors — audience comments and brand replies together'
  }
}

// ── Source scope ─────────────────────────────────────────────────────────────
//
// `source` records WHICH INGEST PATH a comment arrived by. Sky's history has a
// hard seam in it: Apify scrapes up to 2026-02-04, Sprinklr after. The two
// paths see different slices of the same conversation, so the seam is a real
// discontinuity in every trend on this page and Sky must be able to isolate it.
//
// ## Why this is record-level, and why Carbon can still answer it
//
// A source belongs to the comment, not to the video — the same video can carry
// Apify-scraped and Sprinklr-fetched comments. So, exactly like the author
// scope, it rides `LabelQuery.metadataFilters` beside `entity`, narrows the
// comment surfaces, and deliberately leaves the video metrics alone.
//
// That also means it is NOT one of the platinum-only predicates: Carbon's case
// data comes from ListAutoCoderPipelines, whose record preload applies metadata
// filters, so both engines agree on it (see chartDriverGuard.ts). Nothing here
// weakens that guard — the entity refusal is untouched, and this filter is
// never expressed as an entity query.
//
// ## Why absence is never treated as evidence
//
// Metadata filtering is jsonb CONTAINMENT, which cannot match a missing key.
// Every Apify-era record currently has no `source` key at all, so "Apify only"
// matches nothing until the stamping migration has run. There are two ways to
// respond to that and only one is honest:
//
//   - lower "Apify" to "NOT source=sprinklr" — cheap, and wrong: it silently
//     relabels every unstamped record, including any future ingest path that
//     forgets to stamp itself, as Apify;
//   - match the stamped value positively and TELL THE USER how many records
//     carry no source at all.
//
// We do the second. `deriveSourceCoverage` counts the gap from the job's own
// metadata summary and `unlabelledSourceNotice` puts it on screen, so a page
// showing zero Apify comments says why instead of just reading zero. (The
// author scope's absence rule is the opposite way round on purpose: there,
// "no author_type" has a DEFINED meaning — audience — set by contract at
// ingest. Here, a missing source is a known gap in the data, not a meaning.)

// ## Why the toggle list is DERIVED, not hardcoded
//
// Apify and Sprinklr are the two paths we have written code for, but they are
// not the only values `source` can hold: CSV uploads and Carbon-sourced records
// are explicitly allowed to stamp their own true source rather than be
// mislabelled apify. A hardcoded apify/sprinklr pair would give such a cohort
// no toggle AND no notice — invisible in exactly the way this control exists to
// prevent. So the toggles come from the job's own metadata summary, like the
// platform toggles come from the job's own filter-summary.
//
// One value cannot be a toggle: the literal string "all", which is the
// sentinel for "no source constraint". Records carrying it are counted as
// `unreachable` and named in the notice rather than silently dropped.

/**
 * `'all'` is the reserved sentinel for "no source constraint"; any other value
 * is a literal `metadata.source` value taken from the job's own data. A LIST is
 * a multi-select — the values OR together (T3.5).
 *
 * 🔴 THE SENTINEL IS NOT A SHORTHAND FOR "EVERY SOURCE TICKED". The lowering is
 * jsonb containment and containment cannot match a MISSING key, so an explicit
 * full list would silently drop every record carrying no `source` stamp —
 * including any future ingest path that forgets to stamp itself. "All" must
 * send no constraint at all, which is why a full selection is collapsed back to
 * the sentinel by `normalizeSourceScope`. Platform selection follows the same
 * rule for the same reason.
 */
export type SourceScope = string | string[]

/** The record-metadata key the ingest paths stamp: "apify" | "sprinklr" | … */
export const SOURCE_KEY = 'source'

/** The sentinel meaning "no source constraint" — never a real source value. */
export const ALL_SOURCES = 'all'

/**
 * Record-metadata keys owned by the always-visible toolbar controls. The
 * sidebar must not offer them a second time: two controls over one key can
 * disagree, and the one that loses does so silently (the toolbar wins the
 * merge in `buildLabelQuery`). This is the same rule platform already follows.
 */
export const TOOLBAR_OWNED_METADATA_KEYS: readonly string[] = [AUTHOR_TYPE_KEY, SOURCE_KEY]

export function isToolbarOwnedMetadataKey(key: string): boolean {
  return TOOLBAR_OWNED_METADATA_KEYS.includes(key)
}

/**
 * The record-metadata keys a SKY dashboard offers as sidebar facets — today,
 * none (Kai, 2026-08-14).
 *
 * 🔴 AN EMPTY LIST IS THE DECISION, NOT AN OVERSIGHT. Record metadata is what
 * ingest happened to stamp, so discovering the facets from the data puts
 * `parent_post_id`, `permalink` and `platform_comment_id` in the sidebar — keys
 * that are UNIQUE PER RECORD. A facet whose every value has count 1 is not a
 * filter, it is a list of ids the user must scroll past to reach the filters
 * that mean something.
 *
 * `channel` is the one key here worth filtering on, and the always-on toolbar
 * already owns it under the name PLATFORM — so offering it again would be the
 * same one-key-two-controls trap `TOOLBAR_OWNED_METADATA_KEYS` exists to
 * prevent, just spelled differently.
 *
 * That leaves nothing, which is why this is empty. It is a LIST rather than a
 * deleted component because the next key worth offering is a one-line addition
 * here, and because "we choose what Sky sees" is a claim that should be written
 * down somewhere a reader can check.
 */
export const SKY_SIDEBAR_METADATA_KEYS: readonly string[] = []

/**
 * The ENTITY label keys a SKY dashboard offers as `LabelFilterBuilder` sidebar
 * facets — `account`, the one entity label worth a curated Sky product
 * dashboard filtering on directly (per-creator/per-account breakdowns).
 *
 * This is `LabelFilterBuilder`'s `labelKeys` allowlist, the entity-label
 * counterpart to `SKY_SIDEBAR_METADATA_KEYS` above for RECORD metadata — two
 * different facet universes (`useEntityFilterSummary().labels` vs
 * `useRecordMetadata()`), each curated the same way and for the same reason:
 * "we choose what Sky sees" should be a claim this file states, not one an
 * unfiltered facet list makes by omission.
 */
export const SKY_SIDEBAR_LABEL_KEYS: readonly string[] = ['account'] as const

/**
 * The metadata keys the sidebar may render facets for.
 *
 * With no `allow` list this DISCOVERS: every key the mount has, minus the ones
 * the toolbar owns. That is right for a job-level/admin surface exploring
 * unfamiliar data, and wrong for a curated product dashboard — see
 * `SKY_SIDEBAR_METADATA_KEYS`.
 *
 * With an `allow` list the result is the intersection, in ALLOWLIST order (the
 * order is the curator's ranking, not the alphabet's), and a key the mount has
 * no values for is still dropped — an allowlist may name a key this department
 * has never stamped, and an empty facet is a control that can only disappoint.
 * The toolbar-owned keys are refused either way: the allowlist widens nothing.
 */
export function sidebarMetadataKeys(keys: string[], allow?: readonly string[]): string[] {
  const offered = keys.filter((k) => !isToolbarOwnedMetadataKey(k))
  if (!allow) return offered.sort()
  const present = new Set(offered)
  return allow.filter((k) => present.has(k) && !isToolbarOwnedMetadataKey(k))
}

/** Strip the toolbar-owned keys from a record-metadata filter map. */
export function withoutToolbarOwnedKeys(filters: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (!isToolbarOwnedMetadataKey(key)) out[key] = value
  }
  return out
}

export interface SourceOption {
  value: string
  label: string
  count: number
}

/**
 * The source toggles this job needs, busiest first, derived from its own
 * `source` metadata values. The reserved sentinel is never offered as a toggle.
 */
export function deriveSourceOptions(values: LabelValue[] | undefined): SourceOption[] {
  return (values ?? [])
    .filter((v) => v.value !== '' && v.value !== ALL_SOURCES)
    .map((v) => ({ value: v.value, label: sourceLabel(v.value), count: v.count ?? 0 }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
}

/**
 * The ingest path Sky's dashboards are ABOUT.
 *
 * Named as a constant rather than spelled inline because three modules now
 * depend on the same value meaning the same thing — this file's default and
 * normalize fallbacks, `useSocialFilters`' forced scope, and the server's own
 * `productSourceScope` (goapi/pkg/server/sky_social_media.go:566), which is
 * where the rule ultimately lives for the five pinned departments.
 */
export const SPRINKLR_SOURCE = 'sprinklr'

/**
 * The page opens on SPRINKLR, and reaching anything else is a deliberate act.
 *
 * 🔴 CHANGED FROM 'all' (2026-08-24), AND THE OLD VALUE WAS THE PROBLEM.
 * A merged scope answers a question nobody asks: only Sprinklr tells us which
 * campaign a comment belongs to — it rides in on the message itself — while
 * the campaigns that appeared on Apify comments were INHERITED from a
 * Sprinklr-synced parent video, which is a weaker claim wearing the same
 * clothes as a real one (see productSourceScope, sky_social_media.go:547).
 * Adding the two together produces a number whose provenance is half one thing
 * and half another, with nothing on screen saying which half.
 *
 * The five department dashboards were never exposed to this — the server pins
 * them. The `all` admin view was, and this is what closes it.
 */
export const DEFAULT_SOURCE_SCOPE: SourceScope = SPRINKLR_SOURCE

/** Display names for the ingest paths. */
const SOURCE_LABELS: Record<string, string> = { apify: 'Apify', sprinklr: 'Sprinklr' }

export function sourceLabel(value: string): string {
  return SOURCE_LABELS[value.toLowerCase()] ?? platformLabel(value)
}

/** The record-level half of a LabelQuery implied by a source scope. */
export interface SourceScopeQuery {
  metadataFilters?: Record<string, string>
  metadataAny?: Record<string, string[]>
}

/** True when the scope places no constraint at all. */
export function isAllSources(scope: SourceScope): boolean {
  return scope === ALL_SOURCES
}

/**
 * The selected source values, sorted. The sentinel reads as an EMPTY selection
 * because it constrains nothing — callers must not confuse that with the user
 * un-ticking everything, which is `[]` and means the opposite.
 */
export function sourceScopeValues(scope: SourceScope): string[] {
  if (isAllSources(scope)) return []
  if (Array.isArray(scope)) return [...scope].sort()
  return [scope]
}

/**
 * Lower a source scope into its record-level predicate.
 *
 * ONE lowering for both the single and multi cases, deliberately: an equality
 * filter would serve the single case perfectly well, and having two lowerings
 * for one control is how the two drift apart. Everything goes through
 * `metadataAny` (T3.3), which ORs within the key.
 *
 * An explicitly empty selection stays empty — `{source: []}`, which the backend
 * lowers to "match nothing". It must NOT widen to "all": every option un-ticked
 * is a real state the user chose, and widening is the dangerous direction.
 */
export function sourceScopeQuery(scope: SourceScope): SourceScopeQuery {
  if (isAllSources(scope)) return {}
  return { metadataAny: { [SOURCE_KEY]: sourceScopeValues(scope) } }
}

export function describeSourceScope(scope: SourceScope): string {
  const values = sourceScopeValues(scope)
  if (values.length > 1) {
    const names = values.map(sourceLabel)
    return `${names.join(' + ')} — comments from ${names.length} ingest paths, together`
  }
  scope = values.length === 1 ? values[0] : ALL_SOURCES
  switch (scope) {
    case 'apify':
      return 'Apify only — comments scraped directly from the platforms (Sky’s history up to 2026-02-04)'
    case 'sprinklr':
      return 'Sprinklr only — comments delivered by Sky’s Sprinklr feed (from 2026-02-04)'
    case ALL_SOURCES:
      return 'All sources — every ingest path this job has, together'
    default:
      // A source we have written no copy for still gets an honest sentence.
      return `${sourceLabel(scope)} only — comments stamped \`${SOURCE_KEY}\` = “${scope}” at ingest`
  }
}

/**
 * Reconcile a scope (typically restored from storage, or carried across a job
 * switch) with the sources this job actually has.
 *
 * A scope with no toggle would be a filter the user can neither see nor clear,
 * so it falls back to a scope the control CAN draw. While the options are still
 * empty (summary loading) the scope is kept — widening it then would silently
 * unfilter the page.
 *
 * 🔴 BOTH FALLBACKS LAND ON SPRINKLR, NOT ON ALL_SOURCES (changed 2026-08-24).
 * The bar no longer renders a source `All` button, so `ALL_SOURCES` became a
 * state with no toggle to represent it — arriving there would reintroduce the
 * exact unclearable filter this function exists to prevent, just from the
 * opposite direction. `ALL_SOURCES` itself survives because the five pinned
 * departments still resolve to it (useSocialFilters.ts:157), where the server
 * replaces the scope anyway and no control is drawn at all.
 */
export function normalizeSourceScope(scope: SourceScope, options: SourceOption[]): SourceScope {
  if (isAllSources(scope) || options.length === 0) return scope
  const available = new Set(options.map((o) => o.value))
  const kept = sourceScopeValues(scope).filter((v) => available.has(v))
  // Nothing this job can offer: a filter the user can neither see nor clear.
  if (kept.length === 0) return SPRINKLR_SOURCE
  // A FULL selection is not a scope the single-select control can draw either,
  // and it used to collapse to the sentinel. It now collapses to Sprinklr for
  // the same reason: "everything" is no longer one of the buttons.
  if (kept.length === available.size) return SPRINKLR_SOURCE
  return kept.length === 1 ? kept[0] : kept
}

/**
 * How much of what this dashboard covers carries a `source` label at all.
 *
 * **These counts are UNFILTERED, and scoped to the dashboard's own mount.** They
 * come from `/pipelines/metadata-summary`, which counts every record of every
 * normal-mode pipeline with no state, date, platform or author predicate — so
 * they reconcile with NOTHING on the page (the charts and the comment list all
 * read `state = 'completed'`, and the page's own filters narrow further still).
 *
 * ⚠ They are NOT necessarily job-wide any more. T1.2 scoped that endpoint by
 * campaign, so on `/sky-social-media/:product` these are one department's
 * numbers and on `/autocoder/:customer/:job` they are the whole job's. The
 * notice is worded to be true of both — see `unlabelledSourceNotice`.
 *
 * Either way it is a fact about the population, never a quantity subtracted
 * from the view: "N comments here carry no source" is true; "excluding N
 * comments from these numbers" would not be.
 */
export interface SourceCoverage {
  /** Records in scope, all states, all normal-mode pipelines. 0 = not loaded. */
  total: number
  /** Records carrying any `source` value. */
  labelled: number
  /** Records carrying none — invisible to EVERY single-source view. */
  unlabelled: number
  /**
   * Records whose `source` value has no toggle of its own (today only the
   * reserved sentinel `"all"`). Also invisible to every single-source view, so
   * the notice names them rather than letting them vanish.
   */
  unreachable: number
}

/**
 * Work out the labelled/unlabelled split from the job's record-metadata
 * summary. `values` is that summary's entry for `source`, which is ABSENT (not
 * empty) when no record has ever been stamped.
 *
 * The counts come from a different query than the total, so the remainder is
 * clamped at zero: "-4 records have no source" would be worse than saying none.
 */
export function deriveSourceCoverage(
  values: LabelValue[] | undefined,
  totalRecords: number,
): SourceCoverage {
  const total = Math.max(0, totalRecords)
  const labelled = Math.min(total, (values ?? []).reduce((sum, v) => sum + (v.count ?? 0), 0))
  const unreachable = Math.min(
    total,
    (values ?? [])
      .filter((v) => v.value === '' || v.value === ALL_SOURCES)
      .reduce((sum, v) => sum + (v.count ?? 0), 0),
  )
  return { total, labelled, unlabelled: Math.max(0, total - labelled), unreachable }
}

/**
 * What to say about records no single-source view can reach, or null when
 * there are none (or when nothing has loaded — an empty summary is not
 * evidence of a gap).
 *
 * Under a single source the message is a warning: those records cannot appear,
 * so a zero here is not evidence of an empty source. Under "both" the numbers
 * are complete, but the toggles still cannot isolate those records, so it stays
 * on screen as a statement of fact rather than an alarm.
 *
 * 🔴 THE WORDING SAYS "THIS DASHBOARD", NOT "THIS JOB", AND THAT IS LOAD-BEARING.
 * It used to say "this job" and "that count is job-wide", which was true while
 * `/pipelines/metadata-summary` was unscoped. T1.2 scoped it: on a product
 * dashboard these counts are now that PRODUCT's, so the old sentence would have
 * put a department's number under the word "job" — a scoped number keeping an
 * unscoped sentence, which is worse than either alone because it reads as a
 * reconciliation failure against the job totals people already know.
 *
 * "This dashboard" is true on both mounts: the whole job on
 * /autocoder/:customer/:job, one department on /sky-social-media/:product. The
 * second half of each sentence still says the count is not adjusted for the
 * filters above, which is the part that has always mattered — these come from a
 * query with no state, date, platform or author predicate, so they reconcile
 * with nothing else on the page.
 */
export function unlabelledSourceNotice(coverage: SourceCoverage, scope: SourceScope): string | null {
  if (coverage.total === 0) return null
  if (coverage.unlabelled === 0 && coverage.unreachable === 0) return null
  const n = coverage.unlabelled.toLocaleString()
  const extra =
    coverage.unreachable > 0
      ? ` A further ${coverage.unreachable.toLocaleString()} carry a \`${SOURCE_KEY}\` value that has no toggle of its own.`
      : ''
  if (coverage.unlabelled === 0) {
    return (
      `This dashboard covers ${coverage.unreachable.toLocaleString()} of ${coverage.total.toLocaleString()} comments ` +
      `whose \`${SOURCE_KEY}\` value has no toggle of its own, so no single-source view can show them. ` +
      'That count is not adjusted for the filters above.'
    )
  }
  if (scope === ALL_SOURCES) {
    return (
      `This dashboard covers ${n} of ${coverage.total.toLocaleString()} comments with no \`${SOURCE_KEY}\` label. ` +
      'They are included in these numbers, but no single-source view can show them on its own. ' +
      `That count is not adjusted for the filters above.${extra}`
    )
  }
  // A multi-select names every selected source, because "none of them can
  // appear under X" has to name what X actually is for the sentence to be true.
  const selected = sourceScopeValues(scope)
  const names = selected.map(sourceLabel).join(' or ')
  const stamps = selected.map((v) => `“${v}”`).join(' or ')
  return (
    `This dashboard covers ${n} comments with no \`${SOURCE_KEY}\` label at all, and none of them can appear ` +
    `under “${names}” — this view shows only records explicitly stamped ${stamps}, so a low ` +
    'number here is not evidence of a small source. That count is not adjusted for the ' +
    `filters above.${extra}`
  )
}

// ── Persistence ──────────────────────────────────────────────────────────────

export interface DashboardControls {
  platformSelection: PlatformSelection
  authorScope: AuthorScope
  sourceScope: SourceScope
}

export const DEFAULT_DASHBOARD_CONTROLS: DashboardControls = {
  platformSelection: ALL_PLATFORMS,
  authorScope: DEFAULT_AUTHOR_SCOPE,
  sourceScope: DEFAULT_SOURCE_SCOPE,
}

/** Per customer/job: two jobs of the same customer keep separate selections. */
export function dashboardControlsKey(customer: string, job: string, product?: string): string {
  // 🔴 THE PRODUCT REPLACES THE customer/job SCOPE, IT DOES NOT EXTEND IT.
  // Every Sky product shares one customer/job (bprsky/tiktok), so a key built
  // from those alone is the SAME key for all five departments — de-select
  // TikTok on Brand and it silently follows you to Sports. That is trap T-C in
  // its quietest form: no error, no wrong numbers, just a filter you did not
  // set. Keyed on the product, switching departments starts clean.
  return `platinum.social.controls.${product ? product : `${customer}.${job}`}`
}

function parseControls(raw: string | null): DashboardControls {
  if (!raw) return DEFAULT_DASHBOARD_CONTROLS
  try {
    const parsed = JSON.parse(raw) as Partial<DashboardControls>
    // 🔴 ONLY A SINGLE-PLATFORM SUBSET IS RESTORED, and the reason is timing.
    // normalizePlatformSelection would fold a stored multi-select back to `all`
    // — but only once the mount's platform list has ARRIVED, and it returns the
    // selection untouched while that list is still empty. For those first
    // renders the control would show "All" (platformChoice cannot represent two)
    // while platformsForQuery still sent both: the page filtered by a control
    // that said it wasn't filtering. Folding it here means the stored value is
    // never in a state the control cannot draw.
    const sel = parsed.platformSelection
    const stored =
      sel && sel.mode === 'subset' && Array.isArray(sel.platforms)
        ? sel.platforms.filter((p): p is string => typeof p === 'string')
        : []
    const platformSelection: PlatformSelection =
      new Set(stored).size === 1 ? subset(stored) : ALL_PLATFORMS
    const authorScope: AuthorScope =
      parsed.authorScope === 'brand' || parsed.authorScope === 'all' || parsed.authorScope === 'audience'
        ? parsed.authorScope
        : DEFAULT_AUTHOR_SCOPE
    // Any non-empty string is a legitimate source (the toggles are derived
    // from the job's data, so we cannot enumerate them here); the dashboard
    // reconciles a stored source it can no longer offer via
    // `normalizeSourceScope`.
    // A stored scope may be a single value or a multi-select list (T3.5).
    const rawSource = parsed.sourceScope
    const sourceScope: SourceScope = Array.isArray(rawSource)
      ? rawSource.filter((v): v is string => typeof v === 'string' && v !== '')
      : typeof rawSource === 'string' && rawSource !== ''
        ? rawSource
        : DEFAULT_SOURCE_SCOPE
    return { platformSelection, authorScope, sourceScope }
  } catch {
    // Corrupt or hand-edited value: the defaults are always safe.
    return DEFAULT_DASHBOARD_CONTROLS
  }
}

export function loadDashboardControls(
  storage: Storage | undefined,
  customer: string,
  job: string,
  product?: string,
): DashboardControls {
  if (!storage) return DEFAULT_DASHBOARD_CONTROLS
  try {
    return parseControls(storage.getItem(dashboardControlsKey(customer, job, product)))
  } catch {
    // Storage can throw outright (private mode, blocked cookies).
    return DEFAULT_DASHBOARD_CONTROLS
  }
}

export function saveDashboardControls(
  storage: Storage | undefined,
  customer: string,
  job: string,
  controls: DashboardControls,
  product?: string,
): void {
  if (!storage) return
  try {
    storage.setItem(dashboardControlsKey(customer, job, product), JSON.stringify(controls))
  } catch {
    // Persistence is a convenience; never let a quota error break the page.
  }
}

// ── Controls state, keyed by customer/job ────────────────────────────────────
//
// The dashboard component does NOT remount when the route's :customer/:job
// changes, so the controls state has to notice the switch itself. Pairing the
// controls with the storage key they came from makes that a pure comparison
// (and lets the caller reset in render, without an effect).

export interface DashboardControlsState {
  /** The storage key these controls were loaded from. */
  key: string
  controls: DashboardControls
}

export function initialControlsState(
  storage: Storage | undefined,
  customer: string,
  job: string,
  product?: string,
): DashboardControlsState {
  return {
    key: dashboardControlsKey(customer, job, product),
    controls: loadDashboardControls(storage, customer, job, product),
  }
}

/**
 * Return `current` unchanged while it still belongs to this customer/job —
 * identity matters, the caller uses it to decide whether to re-set state — or
 * a freshly loaded state for the new job.
 */
export function resolveControlsForJob(
  current: DashboardControlsState,
  storage: Storage | undefined,
  customer: string,
  job: string,
  product?: string,
): DashboardControlsState {
  if (current.key === dashboardControlsKey(customer, job, product)) return current
  return initialControlsState(storage, customer, job, product)
}

// ── The author scope's own empty state ───────────────────────────────────────
//
// The platform control has a visible dead end ("no platforms selected"). The
// author scope has one too, and it is less obvious: `author_type` is stamped at
// ingest, so a job whose records predate author typing carries the key on no
// record at all. "Brand replies only" then matches nothing and every panel goes
// blank with no stated reason — which reads as a broken page rather than as an
// empty selection. These two helpers give it the same explicit treatment.

/** The minimal shape of a PlatinumData table this module needs. */
interface FreqTableLike {
  cells?: { rows?: Array<{ cell?: Array<{ freq?: number | null }> } | null | undefined> | null } | null
}

/**
 * True only when at least one table HAS loaded and every loaded table is all
 * zeros. Nothing loaded yet is deliberately NOT "no records": an empty state
 * shown during loading is a lie with a short half-life.
 */
export function tablesHaveNoRecords(tables: Array<FreqTableLike | null | undefined>): boolean {
  let sawTable = false
  for (const table of tables) {
    if (!table) continue
    sawTable = true
    for (const row of table.cells?.rows ?? []) {
      for (const cell of row?.cell ?? []) {
        if ((cell?.freq ?? 0) !== 0) return false
      }
    }
  }
  return sawTable
}

/**
 * The explanation for an empty page attributable to the author scope, or null.
 * Under 'all' the scope excludes nothing, so an empty page is someone else's
 * fault and claiming it here would misdirect.
 */
export function authorScopeEmptyNotice(scope: AuthorScope, noRecords: boolean): string | null {
  if (!noRecords || scope === 'all') return null
  if (scope === 'brand') {
    return (
      'Brand replies only, and no comment here is marked as one. Author type is stamped at ingest ' +
      `(record metadata \`${AUTHOR_TYPE_KEY}\`), so records ingested before author typing shipped carry no ` +
      'type at all and count as audience. Switch to “All authors” to see the whole conversation.'
    )
  }
  return (
    'Audience only, and nothing is left after excluding the brand’s own replies ' +
    `(record metadata \`${AUTHOR_TYPE_KEY}\` = “${AUTHOR_TYPE_BRAND}”). Switch to “All authors” to include them.`
  )
}
